import {
  SafeAreaView,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Image,
  ScrollView,
  Animated,
  Dimensions,
} from "react-native";
import React, { useState, useRef } from "react";
import AppColors from "../../Constants/AppColors";
import AppImages from "../../Constants/AppImages";
import AppFonts from "../../Constants/AppFonts";
import FleetAll from "./FleetAll";
import FleetDues from "./FleetDues";
import FleetUpcoming from "./FleetUpcoming";
import FleetHistorical from "./FleetHistorical";

const { width } = Dimensions.get("window");

const FleetMainTab = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState(0);
  const scrollViewRef = useRef(null);
  const translateX = useRef(new Animated.Value(0)).current;

  const t = (key) => {
    if (typeof key === "object" && key["common:translation"]) {
      return key["common:translation"];
    }
    if (typeof key === "string") {
      const parts = key.split(":");
      return parts[parts.length - 1].replace(/^translation./, "");
    }
    return key;
  };

  const tabs = [
    { key: "all", label: "All", component: FleetAll },
    { key: "dues", label: "Dues", component: FleetDues },
    { key: "upcoming", label: "Upcoming", component: FleetUpcoming },
    { key: "history", label: "History", component: FleetHistorical },
  ];

  const handleTabPress = (index) => {
    setActiveTab(index);

    // Animate indicator
    Animated.spring(translateX, {
      toValue: index * (width / tabs.length),
      useNativeDriver: true,
      tension: 100,
      friction: 12,
    }).start();

    // Scroll to tab content
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        x: width * index,
        animated: true,
      });
    }
  };

  const handleScroll = (event) => {
    const contentOffsetX = event.nativeEvent.contentOffset.x;
    const tabIndex = Math.round(contentOffsetX / width);
    if (tabIndex !== activeTab) {
      setActiveTab(tabIndex);
      Animated.spring(translateX, {
        toValue: tabIndex * (width / tabs.length),
        useNativeDriver: true,
      }).start();
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: AppColors.sandal }}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              if (typeof window !== "undefined" && window.history.length > 1) {
                window.history.back();
              } else {
                window.location.href = "/";
              }
            }}
          >
            <Image source={AppImages.Backbtn} style={styles.backIcon} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Fleet Maintenance</Text>
        </View>
        <View style={styles.headerRight}>
          <TouchableOpacity onPress={() => navigation.navigate("/Dashboard/DashboardMap/Alerts/View")}>
            <Image
              source={AppImages.Bellnoti}
              style={styles.notificationIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => navigation.navigate("AddFleetMain")}>
            <Image
              source={AppImages.Plus}
              style={styles.plusIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Custom Tab Bar */}
      <View style={styles.tabBarContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabBarScrollContent}
        >
          <View style={styles.tabBar}>
            {tabs.map((tab, index) => (
              <TouchableOpacity
                key={tab.key}
                style={styles.tabButton}
                onPress={() => handleTabPress(index)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.tabLabel,
                    activeTab === index && styles.activeTabLabel,
                  ]}
                >
                  {tab.label}
                </Text>
              </TouchableOpacity>
            ))}

            {/* Animated Indicator */}
            <Animated.View
              style={[
                styles.indicator,
                {
                  transform: [{ translateX }],
                  width: width / tabs.length,
                },
              ]}
            />
          </View>
        </ScrollView>
      </View>

      {/* Tab Content with Horizontal Scroll */}
      <View style={{ flex: 1 }}>
        <ScrollView
          ref={scrollViewRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          style={styles.contentContainer}
        >
          {tabs.map((tab, index) => (
            <View key={tab.key} style={{ width, flex: 1 }}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
              >
                <tab.component />
              </ScrollView>
            </View>
          ))}
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default FleetMainTab;

const styles = StyleSheet.create({
  header: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: AppColors.white,
    height: 58,
    justifyContent: "space-between",
    paddingHorizontal: 20,
    shadowColor: "#9C4900",
    shadowOffset: { width: 10, height: 10 },
    shadowOpacity: 10,
    shadowRadius: 10,
    elevation: 10,
  },
  headerLeft: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  backButton: {
    height: 30,
    width: 30,
    justifyContent: "center",
  },
  backIcon: {
    width: 8,
    height: 15,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: AppFonts.OverpassMedium,
    fontWeight: "500",
    color: AppColors.brown1,
    marginLeft: 15,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  notificationIcon: {
    width: 16,
    height: 20,
  },
  plusIcon: {
    width: 18,
    height: 18,
    marginLeft: 20,
  },
  tabBarContainer: {
    backgroundColor: AppColors.white,
    marginTop: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
    elevation: 2,
  },
  tabBarScrollContent: {
    flexGrow: 1,
  },
  tabBar: {
    flexDirection: "row",
    position: "relative",
    backgroundColor: AppColors.white,
    minWidth: width,
  },
  tabButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 15,
    paddingHorizontal: 10,
    minWidth: width / 4,
  },
  tabLabel: {
    fontFamily: AppFonts.OverpassRegular,
    fontSize: 14,
    color: "#9C4900CC",
    textAlign: "center",
    includeFontPadding: false,
  },
  activeTabLabel: {
    fontFamily: AppFonts.OverpassBold,
    color: "#8F4300",
  },
  indicator: {
    position: "absolute",
    bottom: 0,
    height: 3,
    backgroundColor: "#8F4300",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: AppColors.sandal,
  },
});
